import { useMemo } from "react";
import { computedGradeFromRuleOverrides } from "@/components/case-checking/overallAssessmentGrade";
import {
  overallReviewNotesBody,
  unpackOverallGrade,
} from "@/components/case-checking/ReviewOverallSignoffCard";
import { displayReviewerName } from "@/components/case-checking/humanReviewCompare";
import {
  caseReviewBaselineGrade,
  caseReviewRollupGrade,
  type CaseCheckingGrade,
} from "@/components/case-checking/caseCheckingStatusBadges";
import { useHumanRuleReviews } from "@/components/case-checking/useHumanRuleReviews";
import { useProblemStatementRun } from "@/components/case-checking/useProblemStatementRun";
import { finalOutcomeId } from "@/components/case-checking/outcomeCatalog";
import type { CaseReview as ApiCaseReview } from "@/services/api/case-checking";

export type ReviewAttribution = {
  reviewerName?: string | null;
  reviewedAt: string;
};

export function useHumanReviewOutcome(
  caseId: string,
  caseReview: ApiCaseReview | null | undefined,
  fallbackGrade: CaseCheckingGrade,
  displayAdviser?: string,
  companyId?: string,
) {
  const overallStatementId = finalOutcomeId(companyId);
  const { run, loading: runLoading, scorable } = useProblemStatementRun(caseId);
  const { overridesByKey, loading: rulesLoading } = useHumanRuleReviews(caseId);

  const savedOverallReview = useMemo(
    () => run?.human_reviews?.find((r) => r.problem_statement_id === overallStatementId),
    [overallStatementId, run?.human_reviews],
  );

  const agentGrade = caseReviewRollupGrade(caseReview ?? null, fallbackGrade);
  const agentBaselineGrade = caseReviewBaselineGrade(caseReview ?? null, fallbackGrade);

  const ruleRollupGrade = useMemo((): CaseCheckingGrade | undefined => {
    if (overridesByKey.size === 0) return undefined;
    return computedGradeFromRuleOverrides(scorable, overridesByKey);
  }, [overridesByKey, scorable]);

  const outcomeGrade = useMemo((): CaseCheckingGrade => {
    if (savedOverallReview?.reviewed_at) {
      return unpackOverallGrade(savedOverallReview, agentGrade);
    }
    return ruleRollupGrade ?? agentGrade;
  }, [agentGrade, ruleRollupGrade, savedOverallReview]);

  const reviewAttribution = useMemo((): ReviewAttribution | null => {
    if (!savedOverallReview?.reviewed_at) return null;
    const reviewerName = displayReviewerName(savedOverallReview.reviewer_name, displayAdviser);
    return {
      reviewerName,
      reviewedAt: savedOverallReview.reviewed_at,
    };
  }, [displayAdviser, savedOverallReview]);

  const humanReviewNotes = useMemo(() => {
    if (!savedOverallReview?.reviewed_at) return null;
    const body = overallReviewNotesBody(savedOverallReview.notes);
    return body || null;
  }, [savedOverallReview]);

  const hasHumanOverallReview = Boolean(savedOverallReview?.reviewed_at);

  return {
    loading: runLoading || rulesLoading,
    agentGrade,
    agentBaselineGrade,
    ruleRollupGrade,
    outcomeGrade,
    reviewAttribution,
    humanReviewNotes,
    hasHumanOverallReview,
    savedOverallReview,
    hasRuleOverrides: overridesByKey.size > 0,
  };
}
